import { computed } from 'vue';

import type { INumberResponse } from '@/openapi-clients-generated/talk';

import { GlobalState } from '@/global';
import { DataCache } from '@/shared/data/cache';
import { hasPermission } from '@/shared/helpers/permissions.helpers';

/**
 * A number the platform has put into emergency fan-out mode.
 *
 * Set by the service when a 911/933 call is routed: for as long as it holds, inbound calls to that
 * number ring *every* phone at its e911 location, so a dispatcher calling back reaches someone. It
 * stays set until an admin clears it — nothing expires it — which is why it needs surfacing rather
 * than just logging.
 */
export function isEmergencyModeActive(number: INumberResponse) {
    return !number.deletedAt && number.emergencyStatus === 'active';
}

/** The live numbers currently in emergency fan-out mode. Pure, so the panes and the table agree. */
export function findEmergencyActiveNumbers(numbers: INumberResponse[]) {
    return numbers.filter(isEmergencyModeActive);
}

/**
 * How many numbers are sitting in emergency fan-out mode.
 *
 * Drives the red marker on the Admin sidebar entry and on Admin → ZynoTalk → Overview, where each
 * one is listed with a Reset action. Grouped with the e911 gaps under `adminHasFault` rather than
 * the attention dot: both are emergency calling, which is the one thing the red marker means.
 *
 * Same shape as its siblings: gated on tenant-admin + Talk, reading the shared `talkNumbers` entry
 * lazily so a nav marker never provokes the request on its own.
 */
export const talkEmergencyActiveCount = computed<number>(() => {
    if (!GlobalState.tenant.value?.isAdmin || !hasPermission('talk')) return 0;
    const numbers = DataCache.getLazyRef('talkNumbers').value;
    return numbers ? findEmergencyActiveNumbers(numbers).length : 0;
});

/** Returns the shared count, kicking the (gated, deduped) load if it hasn't run yet. */
export function useTalkEmergencyActive() {
    if (GlobalState.tenant.value?.isAdmin && hasPermission('talk')) DataCache.getRef('talkNumbers');
    return talkEmergencyActiveCount;
}
